/**
 * Sprint 77 — Cuánto se ha cobrado por llevar a ese rumbo.
 *
 * El negocio describió su tarifa así: *"es por zona (en kilómetros)… Galaxia, Tabasco 2000 desde
 * mi negocio son 50 pesos"*, y en la misma frase puso su regla: *"aunque siempre por regla
 * prefiero cotizar"*.
 *
 * De ahí salen las dos propiedades de este módulo:
 *
 *  1. **Sugiere, no rellena.** Lo que se calcula aquí alimenta botones de atajo; el campo del
 *     costo nace vacío. Un valor precargado se acepta sin leer, y un día sale un pedido con la
 *     tarifa del mes pasado sin que nadie se entere hasta el corte.
 *  2. **Sin internet y sin API de mapas.** La cercanía se calcula con las coordenadas que el
 *     sistema ya guarda de cada entrega. Agrupar por el texto de la colonia no funcionaría:
 *     "Galaxia", "Fracc. Galaxia" y "galaxia" son la misma zona escrita de tres formas, y en
 *     Villahermosa la gente escribe la dirección como se le ocurre.
 *
 * Todo puro: se prueba entero y corre igual en el escritorio y en el track web.
 */

/** Radio en metros dentro del cual dos entregas se consideran "el mismo rumbo". */
export const RADIO_MISMA_ZONA_M = 800;

/** Cuántos envíos anteriores se miran hacia atrás. Más allá, la tarifa ya no es la de hoy. */
export const MAX_ENVIOS_APRENDIDOS = 40;

export interface PuntoEntrega {
  latitud: number;
  longitud: number;
}

export interface EnvioHistorico extends PuntoEntrega {
  /** Centavos cobrados por ese envío. */
  costoEnvio: number;
  /** Cuándo fue, para poder decir "la última vez". */
  fecha: string | Date;
}

export interface TarifaSugerida {
  /** Centavos. */
  tarifa: number;
  /** Cuántas veces se cobró esa cantidad en esa zona. */
  veces: number;
  /** La más reciente de esas veces, en ISO. */
  ultima: string;
}

/**
 * Distancia en metros entre dos puntos (haversine).
 *
 * Es la distancia en línea recta, no la que recorre la moto. Da igual: aquí no se cobra por
 * distancia, se usa para decidir si dos entregas son del mismo rumbo, y para eso sobra.
 */
export function distanciaMetros(a: PuntoEntrega, b: PuntoEntrega): number {
  const R = 6_371_000; // radio de la Tierra en metros
  const rad = Math.PI / 180;
  const dLat = (b.latitud - a.latitud) * rad;
  const dLng = (b.longitud - a.longitud) * rad;
  const lat1 = a.latitud * rad;
  const lat2 = b.latitud * rad;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** ¿Este punto es del mismo rumbo que el otro? */
export function esMismaZona(
  a: PuntoEntrega,
  b: PuntoEntrega,
  radioM: number = RADIO_MISMA_ZONA_M,
): boolean {
  return distanciaMetros(a, b) <= radioM;
}

function aIso(v: string | Date): string {
  return v instanceof Date ? v.toISOString() : new Date(v).toISOString();
}

/**
 * Qué se ha cobrado por llevar a este punto, agrupado por monto.
 *
 * Devuelve las tarifas ordenadas por **frecuencia** —no por la más reciente— porque lo que sirve
 * de referencia es lo habitual, no lo excepcional: el día que llovió se cobró $60 una vez y eso
 * no es la tarifa de la zona. Aun así la excepción se devuelve, con su conteo a la vista, porque
 * a veces la excepción es la nueva tarifa y quien cotiza tiene que poder verla.
 *
 * Los envíos de $0 no cuentan: una cortesía no es una tarifa, es una decisión de ese día.
 */
export function tarifasDeLaZona(
  destino: PuntoEntrega,
  historico: EnvioHistorico[],
  opciones: { radioM?: number; max?: number } = {},
): TarifaSugerida[] {
  const radioM = opciones.radioM ?? RADIO_MISMA_ZONA_M;
  const max = opciones.max ?? MAX_ENVIOS_APRENDIDOS;

  const cercanos = historico
    .filter((h) => Number.isFinite(h.latitud) && Number.isFinite(h.longitud))
    .filter((h) => h.costoEnvio > 0)
    .filter((h) => esMismaZona(destino, h, radioM))
    .sort((a, b) => new Date(aIso(b.fecha)).getTime() - new Date(aIso(a.fecha)).getTime())
    .slice(0, max);

  const porTarifa = new Map<number, TarifaSugerida>();
  for (const envio of cercanos) {
    const previo = porTarifa.get(envio.costoEnvio);
    const iso = aIso(envio.fecha);
    if (previo) {
      previo.veces += 1;
      if (iso > previo.ultima) previo.ultima = iso;
    } else {
      porTarifa.set(envio.costoEnvio, { tarifa: envio.costoEnvio, veces: 1, ultima: iso });
    }
  }

  return [...porTarifa.values()].sort(
    (a, b) => b.veces - a.veces || (a.ultima < b.ultima ? 1 : -1),
  );
}

/**
 * Cómo se le explica al operador de dónde salió el número.
 *
 * Una sugerencia sin procedencia es una cifra que aparece sola, y ésas no se cuestionan: se
 * aceptan. Decir "7 de los últimos envíos aquí" convierte el atajo en un dato que se puede
 * discutir con el grupo de motomandados.
 */
export function explicarTarifa(t: TarifaSugerida): string {
  if (t.veces === 1) return 'una vez';
  return `${t.veces} envíos`;
}
